import { useEffect } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Link from '@mui/material/Link'
import { PERMISSIONS } from '@jupi/shared'
import { AuthCard, LoginForm, useAuth } from '@jupi/ui'
import { applyUserLocale } from '@jupi/ui/i18n'
import Layout from './layout'
import FleetPage from './pages/FleetPage'
import AircraftPage from './pages/AircraftPage'
import PerformPage from './pages/PerformPage'
import PartsPage from './pages/PartsPage'

// Only the member app has a reset-password page; this link goes there when
// the build knows where that is (VITE_MEMBER_APP_URL), and is left out when not.
const MEMBER_APP_URL = (import.meta.env.VITE_MEMBER_APP_URL as string | undefined)?.replace(/\/$/, '')

export default function App() {
  const { t } = useTranslation()
  const { user, loading, can, acceptSession, logout } = useAuth()

  useEffect(() => {
    applyUserLocale(user?.locale)
  }, [user?.locale])

  if (loading) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100dvh', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={56} />
      </Box>
    )
  }

  // The same sign-in as the member app (passwords, email codes, passkeys),
  // on this site's own session. Signing in leaves you on the page you asked for.
  if (!user) {
    return (
      <LoginForm
        title={t('huolto.signInTitle')}
        subtitle={t('huolto.signInSubtitle')}
        onSignedIn={(signedIn) => acceptSession(signedIn)}
        forgotPassword={
          MEMBER_APP_URL ? (
            <Link href={`${MEMBER_APP_URL}/forgot-password`} variant="body1" sx={{ alignSelf: 'center', py: 1 }}>
              {t('auth.forgotPassword')}
            </Link>
          ) : undefined
        }
      />
    )
  }

  // Writing is what the site is for: anyone who can only READ maintenance
  // (every member can) has nothing to do here. The API checks it again.
  if (!can(PERMISSIONS.maintenanceWrite)) {
    return (
      <AuthCard title={t('huolto.signInTitle')}>
        <Alert severity="warning" sx={{ mb: 3 }}>
          {t('huolto.noAccess')}
        </Alert>
        <Button fullWidth variant="contained" size="large" onClick={() => void logout()}>
          {t('huolto.signOut')}
        </Button>
      </AuthCard>
    )
  }

  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<FleetPage />} />
        <Route path="/aircraft/:registration" element={<AircraftPage />} />
        <Route path="/aircraft/:registration/perform" element={<PerformPage />} />
        {/* The inventory permissions are checked by the API as well; without
            them the link is not shown and the page goes home. */}
        <Route
          path="/parts"
          element={can(PERMISSIONS.inventoryRead, PERMISSIONS.inventoryManage) ? <PartsPage /> : <Navigate to="/" replace />}
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  )
}
